import React, { useEffect, useState } from "react";
import api from "../api";
import { useAuth } from "../auth";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Star, MessageSquareText, Shield } from "lucide-react";
import { Link } from "react-router-dom";

function StarRating({ value, onChange, size = 5, readOnly = false }) {
  return (
    <div className="inline-flex gap-1" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          disabled={readOnly}
          onClick={() => onChange?.(i)}
          className={readOnly ? "cursor-default" : "cursor-pointer"}
          data-testid={`star-${i}`}
          aria-label={`${i} étoile${i > 1 ? "s" : ""}`}
        >
          <Star className={`w-${size} h-${size} ${i <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
        </button>
      ))}
    </div>
  );
}

export default function AppReviews() {
  const { user } = useAuth();
  const [data, setData] = useState({ reviews: [], avg: null, count: 0 });
  const [mine, setMine] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    const r = await api.get("/app-reviews");
    setData(r.data);
    if (user) {
      const m = await api.get("/app-reviews/me").catch(() => ({ data: {} }));
      setMine(m.data?.review_id ? m.data : null);
      if (m.data?.review_id) {
        setRating(m.data.rating);
        setComment(m.data.comment || "");
      }
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [user?.user_id]);

  const submit = async () => {
    if (rating < 1 || rating > 5) return toast.error("Note 1-5 requise");
    setBusy(true);
    try {
      await api.post("/app-reviews", { rating, comment });
      toast.success(mine ? "Avis mis à jour" : "Merci pour votre avis !");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="app-reviews-page">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-sky-50 text-sky-800 rounded-full px-3 py-1 text-xs font-medium mb-3">
          <MessageSquareText className="w-3.5 h-3.5" /> Communauté MySwimmingLessons
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
          Avis sur la plateforme
        </h1>
        <p className="text-slate-600 mt-2">
          Partagez votre expérience avec MySwimmingLessons — vos retours aident notre communauté à grandir.
        </p>
        {data.avg !== null && (
          <div className="mt-4 inline-flex items-center gap-2 bg-white rounded-full shadow px-4 py-2" data-testid="avg-rating-card">
            <StarRating value={Math.round(data.avg)} readOnly />
            <span className="font-bold text-slate-900">{data.avg}/5</span>
            <span className="text-xs text-slate-500">({data.count} avis)</span>
          </div>
        )}
      </div>

      {user ? (
        <div className="card-soft p-6 mb-8" data-testid="my-review-card">
          <h2 className="font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit" }}>
            {mine ? "Modifier mon avis" : "Laisser un avis"}
          </h2>
          <div className="flex items-center gap-3 mb-3">
            <span className="text-sm text-slate-600">Votre note :</span>
            <StarRating value={rating} onChange={setRating} />
          </div>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Qu'avez-vous pensé de l'expérience MySwimmingLessons ? (optionnel)"
            rows={4}
            data-testid="my-review-comment"
          />
          <Button onClick={submit} disabled={busy} className="mt-3 rounded-full bg-sky-700 hover:bg-sky-600" data-testid="submit-my-review">
            {busy ? "Envoi…" : mine ? "Mettre à jour" : "Publier mon avis"}
          </Button>
        </div>
      ) : (
        <div className="card-soft p-6 mb-8 text-center" data-testid="login-prompt">
          <p className="text-slate-600 mb-3">Connectez-vous pour partager votre expérience.</p>
          <Link to="/login"><Button className="rounded-full bg-sky-700 hover:bg-sky-600">Se connecter</Button></Link>
        </div>
      )}

      <div className="space-y-4" data-testid="reviews-list">
        {data.reviews.length === 0 && <div className="card-soft p-8 text-center text-slate-500">Soyez le premier à laisser un avis.</div>}
        {data.reviews.map((r) => (
          <div key={r.review_id} className="card-soft p-5" data-testid={`review-${r.review_id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <StarRating value={r.rating} readOnly />
                <span className="font-semibold text-slate-900">{r.user_name}</span>
                <span className="text-xs uppercase tracking-wide bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full">{r.user_role}</span>
              </div>
              <div className="text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString("fr-FR")}</div>
            </div>
            {r.comment && <p className="text-slate-700 mt-2 whitespace-pre-line">{r.comment}</p>}
            {r.admin_reply && (
              <div className="mt-3 border-l-4 border-sky-300 bg-sky-50/50 p-3 rounded-r-xl" data-testid={`admin-reply-${r.review_id}`}>
                <div className="flex items-center gap-2 text-xs text-sky-800 font-semibold mb-1">
                  <Shield className="w-3 h-3" />
                  Réponse de l'équipe MySwimmingLessons
                  {r.admin_reply_at && <span className="text-slate-400 font-normal">— {new Date(r.admin_reply_at).toLocaleDateString("fr-FR")}</span>}
                </div>
                <p className="text-slate-700 text-sm whitespace-pre-line">{r.admin_reply}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
